import React from 'react';
import { LuArrowLeft, LuMail } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { MinimalTiptapEditorNoToolbar } from '@/components/minimal-tiptap';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Page } from '@/components/ui/page';
import { Separator } from '@/components/ui/separator';
import { formatDateTime, useRequiredParams } from 'utils';
import { useHouseholdRoutePaths } from 'utils/hooks/useHouseholdRoutePaths';
import { PageSpinner } from 'widgets';

import { useGlobalAccount } from '../household-queries';
import { useEmailQuery } from './email-queries';

const EmailParticipantDisplay = ({
    label,
    participant,
}: {
    label: string;
    participant?: { name?: string; email?: string };
}) => {
    if (!participant) {
        return null;
    }

    return (
        <div className="flex items-start gap-2">
            <span className="text-muted-foreground min-w-[60px] font-medium">{label}:</span>
            <div>
                <div className="font-medium">{participant.name || 'Unknown'}</div>
                {participant.email && <div className="text-muted-foreground text-sm">{participant.email}</div>}
            </div>
        </div>
    );
};

const EmailParticipantsDisplay = ({
    label,
    participants,
}: {
    label: string;
    participants?: { participants?: Array<{ name?: string; email?: string }> };
}) => {
    if (!participants?.participants || participants.participants.length === 0) {
        return null;
    }

    return (
        <div className="flex items-start gap-2">
            <span className="text-muted-foreground min-w-[60px] font-medium">{label}:</span>
            <div className="flex flex-col gap-1">
                {participants.participants.map((participant, index) => (
                    <div key={index}>
                        <span className="font-medium">{participant.name || 'Unknown'}</span>
                        {participant.email && (
                            <span className="text-muted-foreground text-sm"> ({participant.email})</span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export const HouseholdEmailPage = () => {
    const { globalAccountGUID, emailGUID } = useRequiredParams(['globalAccountGUID', 'emailGUID']);
    const navigate = useNavigate();
    const { MessagesTabRoutePath } = useHouseholdRoutePaths();

    const globalAccountQuery = useGlobalAccount(globalAccountGUID);
    const emailQuery = useEmailQuery(emailGUID);

    if (globalAccountQuery.isLoading || emailQuery.isLoading) {
        return <PageSpinner />;
    }

    if (!emailQuery.data || !globalAccountQuery.data) {
        return null;
    }

    const email = emailQuery.data;

    const handleBack = () => {
        navigate(MessagesTabRoutePath(globalAccountGUID));
    };

    return (
        <Page>
            <main className="flex flex-col gap-4 overflow-hidden p-6 pb-8">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="icon" onClick={handleBack}>
                            <LuArrowLeft className="h-5 w-5" />
                        </Button>
                        <h1 className="flex-1 truncate text-2xl font-bold">{email.subject || '(No Subject)'}</h1>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    <Card>
                        <CardHeader className="space-y-3">
                            <div className="flex items-center gap-2">
                                <LuMail className="text-muted-foreground h-5 w-5" />
                                <span className="text-muted-foreground text-sm">{formatDateTime(email.sentAt)}</span>
                            </div>

                            <Separator />

                            <div className="space-y-2">
                                <EmailParticipantDisplay label="From" participant={email.sender} />

                                <EmailParticipantDisplay label="To" participant={email.recipient} />

                                {email.cc && <EmailParticipantsDisplay label="CC" participants={email.cc} />}

                                {email.bcc && <EmailParticipantsDisplay label="BCC" participants={email.bcc} />}
                            </div>
                        </CardHeader>

                        <Separator />

                        <CardContent className="pt-6">
                            <MinimalTiptapEditorNoToolbar
                                value={email.content || ''}
                                className="w-full border-0 shadow-none"
                                editorContentClassName="p-5"
                                output="html"
                                editable={false}
                                editorClassName="focus:outline-hidden"
                            />
                        </CardContent>
                    </Card>
                </div>
            </main>
        </Page>
    );
};
